import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { ContentCopy as CopyIcon, CalendarMonth as CalendarIcon, Schedule as ScheduleIcon, Link as LinkIcon } from '@mui/icons-material';
import { enhancedApi } from '../api';
import CalendarSlotPicker from './CalendarSlotPicker';

const ScheduleCallDialog = ({ open, onClose, clientId, clientName, onSuccess, onMeetingCreated }) => {
  const [mode, setMode] = useState('schedule'); // 'schedule' | 'link'
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  // Schedule Meeting Now state
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [scheduledSuccess, setScheduledSuccess] = useState(false);
  const [videoLink, setVideoLink] = useState('');

  useEffect(() => {
    if (open) {
      setBookingUrl('');
      setError('');
      setCopied(false);
      setSlots([]);
      setSelectedSlot(null);
      setScheduledSuccess(false);
      setVideoLink('');
      checkCalendarAndFetchTypes();
    }
  }, [open]);

  useEffect(() => {
    if (open && mode === 'schedule' && calendarConnected && clientId) {
      fetchSlots();
    } else {
      setSlots([]);
    }
  }, [open, mode, calendarConnected, clientId, selectedType]);

  const checkCalendarAndFetchTypes = async () => {
    try {
      const [statusRes, typesRes] = await Promise.all([
        enhancedApi.getCalendarStatus(),
        enhancedApi.getMeetingTypes()
      ]);
      setCalendarConnected(statusRes.data?.connected || false);
      setMeetingTypes(typesRes.data?.meeting_types || []);
      if (typesRes.data?.meeting_types?.length) {
        setSelectedType(typesRes.data.meeting_types[0].id);
      }
    } catch (err) {
      setCalendarConnected(false);
      setMeetingTypes([]);
      setError('Failed to load calendar status');
    }
  };

  const handleConnectCalendar = async () => {
    try {
      setLoading(true);
      const res = await enhancedApi.initiateCalendarAuth();
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    if (!clientId) return;
    try {
      setSlotsLoading(true);
      setError('');
      const res = await enhancedApi.getCalendarSlots({
        client_id: clientId,
        meeting_type_id: selectedType || undefined,
        days: 30,
      });
      setSlots(res.data?.slots || []);
      if (res.data?.error) setError(res.data.error);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load available slots');
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!selectedSlot || !clientId) return;
    try {
      setScheduling(true);
      setError('');
      const res = await enhancedApi.createMeeting({
        client_id: clientId,
        meeting_type_id: selectedType || null,
        scheduled_at: selectedSlot.start,
      });
      setScheduledSuccess(true);
      setVideoLink(res.data?.video_link || '');
      onSuccess?.();
      onMeetingCreated?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to schedule meeting');
    } finally {
      setScheduling(false);
    }
  };

  const handleCreateBookingLink = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError('');
      const res = await enhancedApi.createBookingLink(clientId, selectedType || null);
      setBookingUrl(res.data?.booking_url || '');
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create booking link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (bookingUrl) {
      navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon />
          Schedule Call with {clientName || 'Client'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {!calendarConnected ? (
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Connect your Google Calendar to let clients book calls based on your real availability.
            </Typography>
            <Button
              variant="contained"
              onClick={handleConnectCalendar}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CalendarIcon />}
            >
              Connect Google Calendar
            </Button>
          </Box>
        ) : (
          <>
            <Tabs value={mode} onChange={(_, v) => setMode(v)} sx={{ mb: 2, minHeight: 36 }}>
              <Tab value="schedule" label="Schedule Meeting Now" icon={<ScheduleIcon />} iconPosition="start" />
              <Tab value="link" label="Create Booking Link" icon={<LinkIcon />} iconPosition="start" />
            </Tabs>
            {mode === 'schedule' ? (
              scheduledSuccess ? (
                <Box>
                  <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                    Meeting scheduled successfully! Check your calendar for the invite.
                  </Typography>
                  {videoLink && (
                    <Button variant="contained" href={videoLink} target="_blank" rel="noopener" sx={{ mt: 1 }}>
                      Join Video Call
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick an available slot from your calendar to schedule the call now.
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Meeting Type</InputLabel>
                    <Select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      label="Meeting Type"
                    >
                      {meetingTypes.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name} ({t.duration_minutes} min)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {slotsLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : slots.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No available slots in the next 14 days. Try creating a booking link instead.
                    </Typography>
                  ) : (
                    <>
                      <CalendarSlotPicker
                        slots={slots}
                        selectedSlot={selectedSlot}
                        onSelectSlot={setSelectedSlot}
                        disabled={scheduling}
                      />
                      <Button
                        variant="contained"
                        onClick={handleScheduleMeeting}
                        sx={{ mt: 2 }}
                        disabled={!selectedSlot || scheduling}
                        startIcon={scheduling ? <CircularProgress size={20} /> : <CalendarIcon />}
                      >
                        {scheduling ? 'Scheduling…' : 'Schedule Meeting'}
                      </Button>
                    </>
                  )}
                </Box>
              )
            ) : bookingUrl ? (
              <Box>
                <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                  Share this link with the client. They can pick a time that works for them.
                </Typography>
                <TextField
                  fullWidth
                  value={bookingUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopy} edge="end">
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                {copied && <Typography variant="caption" color="success.main">Copied!</Typography>}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a booking link for the client. They will see your available times and pick one.
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Meeting Type</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    label="Meeting Type"
                  >
                    {meetingTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.duration_minutes} min)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleCreateBookingLink}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CalendarIcon />}
                >
                  Create Booking Link
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{bookingUrl || scheduledSuccess ? 'Done' : 'Cancel'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleCallDialog;
